/**
 * State for the global create dialog.
 *
 * It lives apart from `create-dialog.tsx` for two reasons: list pages that only
 * need to *open* the dialog (the sidebar renders it) would otherwise pull the
 * whole composer module graph into their chunk, and the open/close rules below
 * are then unit-testable without a DOM — the pattern this feature already uses
 * for `prompt-composer-state.ts`.
 */

import { createStore, useStore } from 'zustand'

import type { ComposerMode } from '#features/common/components/prompt-composer-state.ts'

/**
 * The tab the dialog opens on unless a caller asks for another one. It matches
 * the home screen composer's default, so the dialog opened from the sidebar is
 * the home screen composer in every respect.
 */
export const DEFAULT_CREATE_DIALOG_MODE: ComposerMode = 'implementation'

interface CreateDialogState {
  open: boolean
  /** The composer tab to show — read once per open, since the dialog unmounts its content on close. */
  mode: ComposerMode
  actions: {
    open: (mode?: ComposerMode) => void
    close: () => void
  }
}

export const createDialogStore = createStore<CreateDialogState>((set) => ({
  open: false,
  mode: DEFAULT_CREATE_DIALOG_MODE,
  actions: {
    open: (mode = DEFAULT_CREATE_DIALOG_MODE) => set({ open: true, mode }),
    // Deliberately leaves `mode` alone: the dialog animates out, and resetting
    // here would visibly flip the tab on the way. Nothing goes stale because
    // every `open` sets the mode it wants.
    close: () => set({ open: false }),
  },
}))

/** Stable open/close actions — safe to depend on without re-rendering on state changes. */
export const useCreateDialogActions = () =>
  useStore(createDialogStore, (state) => state.actions)

/** The dialog's visibility and tab. Only the dialog itself needs these. */
export const useCreateDialogState = () => {
  const open = useStore(createDialogStore, (state) => state.open)
  const mode = useStore(createDialogStore, (state) => state.mode)
  return { open, mode }
}
